/**
 * AI Product Mapping — run state machine (super-admin surface, flag
 * `omProd722AiCatalogMappingAssistant`).
 *
 * The SSE run is NOT modeled as RTK
 * Query — a streaming run with a mid-stream column-review pause/resume, batched
 * client-side row merging, manual edits, and policy re-bucketing doesn't fit RTK
 * Query's request/response cache. The stream is driven by the thunks in
 * `./runController`, which dispatch these reducers; the request/response calls
 * (history, detail, cancel, confirm, search) are the RTK Query slice in
 * `./api/productMatching`. Selectors live in `./runSelectors`; the row-review
 * mutation semantics live in `./lib/rowReview`.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  EVENTS_CAP,
  PREP_STAGE_RANK,
  ROW_STATUS,
  STAGE_APPLYING_DECISIONS,
  STAGE_JUDGING_PRODUCTS,
  STAGE_MATCHING_PRODUCTS,
} from './lib/constants';

// Stages that OVERLAP the row stream: the judge runs concurrently with the row pass, and
// the decision sweep interleaves its per-chunk progress with the row frames it rewrites.
// A row flush must not wipe their status line back to a bare "Processing…".
const holdsAcrossRows = (stage: string | undefined): boolean =>
  stage === STAGE_JUDGING_PRODUCTS || stage === STAGE_APPLYING_DECISIONS;
import { mappedColumns } from './lib/columnMapping';
import { DEFAULT_MATCH_POLICY } from './lib/matchPolicy';
import * as rowReview from './lib/rowReview';
import type {
  GridRow,
  MappedColumn,
  MatchPolicy,
  RailEvent,
  RunStatus,
  RunTotals,
} from './lib/types';
import { groupReducers } from './runSliceGroups';
import { restoreReducers } from './runSliceRestore';
import type { RowPatch } from './lib/runEvents';
import type { ColumnResolution, RunContext, TypeNounProposal } from './api/types';
import type { RunGroupItem } from './api/groupTypes';

interface RunMeta {
  id: string | null;
  fileId: string | null;
  fileName: string | null;
  fileSizeBytes: number;
  rowCount: number;
  catalogType: string | null; // 'merchant' | 'vendor'
  context: RunContext | null;
  status: RunStatus;
  /** Pipeline the run executes: 'product_first' | 'row_first'; null until known. */
  mode: string | null;
  /** Groups still awaiting a decision, as the server counted them at park time — the
   *  authoritative number even when the client missed verdict frames. Null off product-first. */
  pendingGroups: number | null;
  /** The server stamped rows_completed_at (`rows_completed` frame) — the judge tail is
   *  open and group decisions are accepted on a still-Processing run. */
  rowsComplete: boolean;
  /** Product groups the funnel found in this file, latched off the `matching_products` stage
   *  heartbeat. The review banner's denominator: the number of groups RECEIVED so far is not it,
   *  and using that made the banner's total climb 1 → 740 while verdicts streamed. 0 until the
   *  funnel reports (a restored run falls back to its stored group count). */
  groupTotal: number;
  processedCount: number;
  totals: RunTotals | null;
  truncated: boolean;
  errorCode: string | null;
  errorMessage: string | null;
}

export interface ProductMappingRunState {
  phase: 'upload' | 'processing';
  run: RunMeta;
  headers: string[];
  detected: MappedColumn[];
  columnResolution: ColumnResolution | null;
  columnTimeoutAt: number | null; // epoch ms deadline to confirm columns, or null when none set
  /** Post-confirmation preparation stage (type analysis / AI classification) — the run is
   *  busy before any row frame exists; null once row matching starts. */
  prepStage: { stage: string; processed: number; total: number | null } | null;
  /** Type nouns this file uses that the taxonomy doesn't know — reviewed in the column dialog. */
  typeProposals: TypeNounProposal[];
  /** The closed family list an approved noun files under (the dialog's family picker). */
  typeFamilies: string[];
  events: RailEvent[];
  rows: GridRow[];
  /** Product-first groups keyed by groupKey — upserted by group_verdict frames and the
   *  restore read, patched by group_state frames and decision responses. Empty on
   *  row-first runs, which is also what keeps their grid byte-identical to today. */
  groups: Record<string, RunGroupItem>;
  policy: MatchPolicy;
  review: boolean;
  reviewLoading: boolean;
  /** True once THIS SESSION made a row-level mark (confirm / edit / no-match) — the
   *  leave-guard's signal on restored runs, where row flags alone can't tell a persisted
   *  decision (loaded confirmed from the server) from unsaved page-local work. */
  rowsTouched: boolean;
  reviewError: string | null;
  /** The on-demand AI review over the run's pending groups (advisory suggestions). */
  aiReview: AiReviewState;
  /** The restore's group page-walk progress; null when no walk is running. Drives the
   *  "loading groups X / Y" line and keeps the product-first surface on its loader until
   *  the first page lands. */
  groupsLoading: { received: number; total: number } | null;
  /** Group keys whose member rows are being fetched right now (expand / re-expand) —
   *  the header spinner and the placeholder-row pulse read this. */
  groupRowsLoading: Record<string, true>;
}

/** Lifecycle of the on-demand AI review: 'running' paints the thinking chips on pending
 *  groups without a suggestion yet; the tallies fill in from ai_review_done (or restore). */
export interface AiReviewState {
  status: 'idle' | 'running' | 'done' | 'failed';
  attempted: number;
  total: number;
  suggested: number;
  none: number;
  /** Offered but never answered (failed batch, budget cut) — a re-run covers these first. */
  unanswered: number;
  error: string | null;
}

export const makeAiReview = (): AiReviewState => ({
  status: 'idle',
  attempted: 0,
  total: 0,
  suggested: 0,
  none: 0,
  unanswered: 0,
  error: null,
});

export const makeRun = (): RunMeta => ({
  id: null,
  fileId: null,
  fileName: null,
  fileSizeBytes: 0,
  rowCount: 0,
  catalogType: null,
  context: null,
  status: 'idle',
  mode: null,
  pendingGroups: null,
  rowsComplete: false,
  groupTotal: 0,
  processedCount: 0,
  totals: null,
  truncated: false,
  errorCode: null,
  errorMessage: null,
});

export const makeInitialState = (): ProductMappingRunState => ({
  phase: 'upload',
  run: makeRun(),
  headers: [],
  detected: [],
  columnResolution: null,
  columnTimeoutAt: null,
  prepStage: null,
  typeProposals: [],
  typeFamilies: [],
  events: [],
  rows: [],
  groups: {},
  policy: { ...DEFAULT_MATCH_POLICY },
  review: false,
  reviewLoading: false,
  reviewError: null,
  rowsTouched: false,
  aiReview: makeAiReview(),
  groupsLoading: null,
  groupRowsLoading: {},
});

// A placeholder grid row for server-driven runs (e.g. re-run) where the client
// never parsed the file — the SSE stream fills these in by idx as it arrives.
export const blankRow = (idx: number): GridRow => ({
  idx,
  fields: {},
  raw: [],
  status: ROW_STATUS.PENDING,
  confidence: 0,
  matchedSku: null,
  matchedTitle: null,
  candidates: [],
  confirmed: false,
  edited: false,
  errorMsg: null,
});

export interface StartRunPayload {
  /** Known up front only on re-attach — a fresh run learns its id from `run_started`. */
  runId?: string | null;
  fileId?: string | null;
  fileName?: string | null;
  fileSizeBytes?: number;
  rows?: GridRow[];
  headers?: string[];
  catalogType?: string | null;
  context?: RunContext | null;
  /** The server's pre-pass row total, known up front on re-attach (from run detail) so the
   *  progress denominator is right from the first frame instead of ramping as rows re-stream. */
  totalRows?: number | null;
  /** Pipeline requested for the run ('product_first' | 'row_first'). */
  mode?: string | null;
}

const runSlice = createSlice({
  name: 'productMappingRun',
  initialState: makeInitialState(),
  reducers: {
    // Begin a new run: reset everything, mark processing/connecting. Server-driven —
    // the grid is built from the stream, so a client row list is optional.
    runInitiated: (_state, { payload }: PayloadAction<StartRunPayload>) => ({
      ...makeInitialState(),
      phase: 'processing' as const,
      run: {
        ...makeRun(),
        id: payload.runId || null,
        fileId: payload.fileId || null,
        fileName: payload.fileName ?? null,
        fileSizeBytes: payload.fileSizeBytes || 0,
        // Prefer the server's pre-pass total (present on re-attach) so the denominator is
        // correct from the first frame; fresh uploads have only the client-parsed rows.
        rowCount: payload.totalRows || payload.rows?.length || 0,
        catalogType: payload.catalogType ?? null,
        context: payload.context || null,
        status: 'connecting' as RunStatus,
        mode: payload.mode ?? null,
      },
      headers: payload.headers || [],
      rows: payload.rows || [],
    }),

    runStarted: (state, { payload }: PayloadAction<{ runId?: string | null }>) => {
      state.run.id = payload.runId || state.run.id;
      state.run.status = 'running';
    },

    columnsDetected: (state, { payload }: PayloadAction<MappedColumn[]>) => {
      state.detected = payload;
    },

    // Backend resolved columns and paused; surface them and await confirmation.
    // Headers come from the server resolution (the grid + export are server-driven).
    columnsProposed: (
      state,
      {
        payload,
      }: PayloadAction<{
        resolution: ColumnResolution;
        timeoutAt?: number | null;
        typeProposals?: TypeNounProposal[] | null;
        typeFamilies?: string[] | null;
        totalRows?: number | null;
      }>,
    ) => {
      state.columnResolution = payload.resolution;
      state.columnTimeoutAt = payload.timeoutAt ?? null;
      state.typeProposals = payload.typeProposals ?? [];
      state.typeFamilies = payload.typeFamilies ?? [];
      state.headers = payload.resolution?.headers || state.headers;
      state.detected = mappedColumns(payload.resolution);
      // The server counted the file during its pre-pass — record the real total so the
      // progress card and file strip show it immediately. The grid is deliberately NOT
      // padded with placeholder rows here: matching hasn't started, so filling it with
      // thousands of empty pulsing rows through column confirmation and the type pre-pass
      // is just noise (and reads as hung). runProgress / rowsMatched materialize the grid
      // to this total the moment the first row frame lands.
      if (payload.totalRows && payload.totalRows > state.run.rowCount) {
        state.run.rowCount = payload.totalRows;
      }
      state.run.status = 'awaiting_columns';
    },

    // User confirmed (possibly edited) the mapping; resume processing.
    columnsConfirmed: (state, { payload }: PayloadAction<ColumnResolution | null | undefined>) => {
      const resolution = payload || state.columnResolution;
      state.columnResolution = resolution;
      state.columnTimeoutAt = null; // confirmed → the confirm deadline no longer applies
      state.typeProposals = []; // decided (or deliberately skipped) with the confirmation
      state.typeFamilies = [];
      state.headers = resolution?.headers || state.headers;
      state.detected = resolution ? mappedColumns(resolution) : state.detected;
      state.run.status = 'running';
    },

    // A preparation-stage heartbeat (analyzing_types / classifying_types) — never row
    // progress, so it must not touch processedCount or pad the grid.
    stageProgress: (
      state,
      { payload }: PayloadAction<{ stage: string; processed: number; total: number | null }>,
    ) => {
      // Latch the funnel's group total while it is on the wire. `prepStage` is cleared the
      // moment row progress starts, and the LATER stage (judging_products) reports a smaller
      // total of its own — so reading the group count off prepStage at any other time is wrong.
      if (payload.stage === STAGE_MATCHING_PRODUCTS && payload.total) {
        state.run.groupTotal = payload.total;
      }
      // The type classification overlaps the group funnel server-side, so its heartbeats
      // interleave with matching_products frames — an earlier stage must not steal the
      // status line back from a later one (its own rail line keeps ticking regardless).
      const rank = (stage: string) => PREP_STAGE_RANK[stage] ?? 0;
      if (state.prepStage && rank(payload.stage) < rank(state.prepStage.stage)) return;
      state.prepStage = payload;
    },

    runProgress: (state, { payload }: PayloadAction<{ processedCount: number; total: number }>) => {
      // Real row progress — preparation is over. Row-overlapping stages are the exception
      // (clearing and re-setting per frame is what made the strip flip-flop). The strip
      // decides who owns the status line: rows while they stream, the judge/sweep once
      // they land.
      if (!holdsAcrossRows(state.prepStage?.stage)) state.prepStage = null;
      // Server-driven runs (re-run) begin with no client-parsed rows; materialize
      // the grid up to the server total so the progress denominator and grid are correct.
      if (payload.total > state.rows.length) {
        while (state.rows.length < payload.total) state.rows.push(blankRow(state.rows.length + 1));
      }
      state.run.processedCount = payload.processedCount;
    },

    // Merge a batch of row results into the grid by idx — O(batch). Grows the grid
    // to fit an idx the client never parsed (server-driven re-run). Also how a past
    // run's stored pages stream into the placeholder grid during a detail restore.
    rowsMatched: (state, { payload }: PayloadAction<RowPatch[]>) => {
      // Row-overlapping stages survive here for the same reason as in runProgress: the
      // sweep's own rewritten-row frames flush through this reducer between its per-chunk
      // progress frames, and each flush used to erase the "Applying group decisions" line
      // back to a bare "Processing…" for most of every chunk.
      if (payload.length > 0 && !holdsAcrossRows(state.prepStage?.stage)) state.prepStage = null;
      for (const update of payload) {
        const i = update.idx - 1;
        if (i < 0) continue;
        while (state.rows.length <= i) state.rows.push(blankRow(state.rows.length + 1));
        state.rows[i] = { ...state.rows[i], ...update } as GridRow;
      }
    },

    eventLogged: (state, { payload }: PayloadAction<RailEvent>) => {
      state.events.push(payload);
      if (state.events.length > EVENTS_CAP) {
        state.events = state.events.slice(state.events.length - EVENTS_CAP);
      }
    },

    // A preparation-stage rail line (analyzing_types / classifying_types). Unlike
    // eventLogged, this UPSERTS by stage: the first heartbeat creates the line, later ones
    // rewrite it in place so it ends on the stage's final value instead of freezing at the
    // first heartbeat. The existing entry's id and ts are kept — the line stays anchored
    // where the phase began and React reuses its node — while segments/level/data are
    // replaced with the latest reading.
    stageRailLogged: (state, { payload }: PayloadAction<RailEvent>) => {
      const stage = (payload.data as { stage?: string } | undefined)?.stage;
      const index = stage
        ? state.events.findIndex(
            (e) => e.kind === 'stage' && (e.data as { stage?: string } | undefined)?.stage === stage,
          )
        : -1;
      const existing = index >= 0 ? state.events[index]! : undefined;
      if (existing) {
        existing.segments = payload.segments;
        existing.level = payload.level;
        existing.data = payload.data;
        // The judge stage OVERLAPS row processing: anchored where it began, its ticking
        // line drowns under a hundred row-progress lines while the rail follows the tail.
        // Re-anchor it to the live edge on every heartbeat instead — the id is kept so
        // React reuses the node; prep stages stay anchored (they run exclusively, so their
        // line already IS the tail while active).
        if (stage === STAGE_JUDGING_PRODUCTS) {
          existing.ts = payload.ts;
          state.events.splice(index, 1);
          state.events.push(existing);
        }
        return;
      }
      state.events.push(payload);
      if (state.events.length > EVENTS_CAP) {
        state.events = state.events.slice(state.events.length - EVENTS_CAP);
      }
    },

    runComplete: (state, { payload }: PayloadAction<{ totals?: RunTotals | null; truncated?: boolean }>) => {
      state.run.status = 'complete';
      state.run.totals = payload.totals || null;
      state.run.truncated = Boolean(payload.truncated);
      state.run.processedCount = payload.totals?.rows ?? state.rows.length;
    },

    // --- Product-first groups (verdicts stream and firm up; decisions flow back) ---
    ...groupReducers,

    // The EFFECTIVE pipeline, once the worker's grouping gate has spoken. The start
    // payload only carries what the client REQUESTED, and the server may fall back to
    // row-first (funnel disabled, gate failed) — this frame is what makes the review
    // surface follow the run that actually executed.
    runModeResolved: (state, { payload }: PayloadAction<{ mode?: string | null }>) => {
      state.run.mode = payload.mode || null;
    },

    // The `rows_completed` frame — the row pass is over and the server accepts group
    // decisions on the judge tail.
    rowsCompleted: (state) => {
      state.run.rowsComplete = true;
    },

    // The park handoff: verdicts are in, the stream closed non-terminally, review moves
    // to REST. Totals carry the pending counter so the tallies never read "all nomatch".
    runAwaitingProducts: (
      state,
      { payload }: PayloadAction<{ totals?: RunTotals | null; pendingGroups?: number }>,
    ) => {
      state.run.status = 'awaiting_products';
      state.run.pendingGroups = payload.pendingGroups ?? state.run.pendingGroups;
      if (payload.totals) {
        state.run.totals = payload.totals;
        state.run.processedCount = payload.totals.rows ?? state.run.processedCount;
      }
    },

    // Recomputed totals with no status change — what a group decision returns. Kept
    // separate from runAwaitingProducts so applying a decision can never re-park a run.
    totalsUpdated: (state, { payload }: PayloadAction<{ totals: RunTotals }>) => {
      state.run.totals = payload.totals;
      state.run.processedCount = payload.totals.rows ?? state.run.processedCount;
    },

    runErrored: (state, { payload }: PayloadAction<{ code?: string | null; message?: string }>) => {
      state.run.status = 'error';
      state.run.errorCode = payload.code || null;
      state.run.errorMessage = payload.message ?? null;
    },

    // Cancel stops the run but keeps the slice intact so the partial results stay
    // on screen; the thunk tears down the stream and asks the server to stop.
    runCancelled: (state) => {
      state.run.status = 'cancelled';
    },

    // --- Row review mutations (single + bulk semantics live in lib/rowReview) ---
    confirmRow: (state, { payload }: PayloadAction<rowReview.ConfirmRowPayload>) => {
      rowReview.confirmRow(state.rows, payload);
      state.rowsTouched = true;
    },

    editRow: (state, { payload }: PayloadAction<rowReview.EditRowPayload>) => {
      rowReview.editRow(state.rows, payload);
      state.rowsTouched = true;
    },

    markNoMatch: (state, { payload }: PayloadAction<{ idx: number }>) => {
      rowReview.markNoMatch(state.rows, payload);
      state.rowsTouched = true;
    },

    confirmRows: (state, { payload }: PayloadAction<{ idxs: number[] }>) => {
      rowReview.confirmRows(state.rows, payload.idxs);
      state.rowsTouched = true;
    },

    markNoMatchRows: (state, { payload }: PayloadAction<{ idxs: number[] }>) => {
      rowReview.markNoMatchRows(state.rows, payload.idxs);
      state.rowsTouched = true;
    },

    editRows: (state, { payload }: PayloadAction<rowReview.EditRowsPayload>) => {
      rowReview.editRows(state.rows, payload);
      state.rowsTouched = true;
    },

    setMatchPolicy: (state, { payload }: PayloadAction<Partial<MatchPolicy>>) => {
      state.policy = { ...state.policy, ...payload };
    },

    // --- Read-only restore of a past run (stored-page walk) ---
    ...restoreReducers,

    reset: () => makeInitialState(),
  },
});

export const productMappingRunReducer = runSlice.reducer;
export const runActions = runSlice.actions;
